import { useState } from "react";
import { clearSavedFilters } from "@/lib/filterUtils";
import { Link, useNavigate } from "react-router-dom";
import { 
  Menu, 
  LogOut,
  X,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

interface DashboardHeaderProps {
  onRefresh?: () => void;
}

const DashboardHeader = ({ onRefresh }: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    // Clear saved filters from localStorage
    clearSavedFilters();
    
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <header className="bg-background border-b border-border sticky top-0 z-40">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Left section - Logo & Mobile Menu */}
        <div className="flex items-center">
          {/* Mobile menu trigger */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden mr-2">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-card text-card-foreground">
              <div className="flex justify-between items-center mb-6">
                <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
                  <img 
                    src="/logo.svg" 
                    alt="RaastaFix Admin Logo" 
                    className="h-8 w-auto" 
                  />
                  <span className="text-foreground font-semibold text-lg">RaastaFix Admin</span>
                </Link>
                <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="space-y-4">
                <Link 
                  to="/dashboard" 
                  className="flex items-center p-2 rounded-md hover:bg-muted"
                  onClick={() => setIsOpen(false)}
                >
                  <span>Dashboard</span>
                </Link>
                <Link 
                  to="/dashboard" 
                  className="flex items-center p-2 rounded-md hover:bg-muted"
                  onClick={() => setIsOpen(false)}
                >
                  <span>Reported Issues</span>
                </Link>
                <Link 
                  to="/dashboard" 
                  className="flex items-center p-2 rounded-md hover:bg-muted"
                  onClick={() => setIsOpen(false)}
                >
                  <span>Analytics</span>
                </Link>
                <Button 
                  variant="ghost" 
                  className="flex items-center w-full justify-start p-2 rounded-md hover:bg-muted"
                  onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="/logo.svg" 
              alt="RaastaFix Admin Logo" 
              className="h-8 w-auto" 
            />
            <span className="text-foreground font-semibold text-lg hidden sm:inline">RaastaFix Admin</span>
          </Link>
        </div>

        {/* Spacer for layout balance */}
        <div className="flex-1"></div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-2">
          {/* Refresh button */}
          {onRefresh && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onRefresh}
              title="Refresh dashboard data"
              className="hover:bg-muted"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          
          {/* User menu simplified to just logout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/10 text-primary">AD</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="flex items-center justify-start p-2">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">Admin User</p>
                  <p className="text-xs leading-none text-muted-foreground">admin@raastafix.com</p>
                </div>
              </div>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;